import { useEffect, useState, useRef, useId } from "react";
import { Button, Card, Checkbox, Flex, Text } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { enumBoxStyleSet, enumHierarchyTypes } from "@coreapp/constants";
import type { BoxStyleEntry } from "./editedOrderUtils";
import { chartModeTestId } from "@/appComponents/Navigation/RightSidebar/Utils/chartModeTestId";

// Filter key enums
export const FILTER_TYPE = {
  APPLIED: "applied",
  NOT_APPLIED: "not-applied",
  SYSTEM: "system",
  SHARED: "shared",
  EMPLOYEE: "employee",
  POSITION: "position",
  GROUP: "group"
} as const;

export type FilterKey = (typeof FILTER_TYPE)[keyof typeof FILTER_TYPE];

export const FILTER_KEYS = Object.values(FILTER_TYPE) as FilterKey[];

interface StoredFilterState {
  activeFilters: FilterKey[];
  searchQuery: string;
}

const isFilterKey = (value: string): value is FilterKey => {
  return FILTER_KEYS.includes(value as FilterKey);
};

const getStoredFilterState = (storageKey: string): StoredFilterState | null => {
  try {
    const rawState = sessionStorage.getItem(storageKey);
    if (!rawState) return null;
    const parsedState = JSON.parse(rawState);
    if (
      !Array.isArray(parsedState?.activeFilters) ||
      typeof parsedState?.searchQuery !== "string"
    ) {
      return null;
    }

    const validFilters = parsedState.activeFilters.filter((key: string) => isFilterKey(key));
    return {
      activeFilters: validFilters.length > 0 ? validFilters : FILTER_KEYS,
      searchQuery: parsedState.searchQuery
    };
  } catch {
    return null;
  }
};

interface BoxStyleFiltersProps {
  activeFilters: Set<FilterKey>;
  toggleFilter: (key: FilterKey) => void;
  onResetFilters: () => void;
  isPositionChart: boolean;
  canAccessGrouping: boolean;
}

type FilterItemProps = {
  label: string;
  checked: boolean;
  onToggle: () => void;
  dataTestId?: string;
};

const FilterItem = ({ label, checked, onToggle, dataTestId }: FilterItemProps) => {
  const id = useId();

  return (
    <Checkbox
      id={id}
      data-testid={dataTestId}
      checked={checked}
      onToggle={(_: any) => onToggle()}
      label={<Text size="small">{label}</Text>}
      size={"medium"}
    />
  );
};

interface FilterSectionProps {
  label: string;
  children: React.ReactNode;
}

const FilterSection = ({ label, children }: FilterSectionProps) => (
  <>
    <Flex direction="column" gap="12px">
      <Text size="xsmall" modifier="medium" colorModifier="grey">
        {label}
      </Text>
      {children}
    </Flex>
  </>
);

const BoxStyleFilters = ({
  activeFilters,
  toggleFilter,
  onResetFilters,
  isPositionChart,
  canAccessGrouping
}: BoxStyleFiltersProps) => {
  const showTypeFilters = isPositionChart || canAccessGrouping;

  return (
    <Card withShadow className="ocn-box-styles-filter-panel">
      <Flex direction="column" gap="16px">
        <FilterSection label={appstr("Applied to views")}>
          <FilterItem
            label={appstr("Applied to views")}
            checked={activeFilters.has(FILTER_TYPE.APPLIED)}
            onToggle={() => toggleFilter(FILTER_TYPE.APPLIED)}
            dataTestId={chartModeTestId("sidebar", "box-styles-filter-applied-checkbox")}
          />
          <FilterItem
            label={appstr("Not applied to views")}
            checked={activeFilters.has(FILTER_TYPE.NOT_APPLIED)}
            onToggle={() => toggleFilter(FILTER_TYPE.NOT_APPLIED)}
            dataTestId={chartModeTestId("sidebar", "box-styles-filter-not-applied-checkbox")}
          />
        </FilterSection>
        <FilterSection label={appstr("Box style library")}>
          <FilterItem
            label={appstr("System")}
            checked={activeFilters.has(FILTER_TYPE.SYSTEM)}
            onToggle={() => toggleFilter(FILTER_TYPE.SYSTEM)}
            dataTestId={chartModeTestId("sidebar", "box-styles-filter-system-checkbox")}
          />
          <FilterItem
            label={appstr("Shared")}
            checked={activeFilters.has(FILTER_TYPE.SHARED)}
            onToggle={() => toggleFilter(FILTER_TYPE.SHARED)}
            dataTestId={chartModeTestId("sidebar", "box-styles-filter-shared-checkbox")}
          />
        </FilterSection>
        {showTypeFilters && (
          <FilterSection label={appstr("Box style type")}>
            <FilterItem
              label={appstr("Employee")}
              checked={activeFilters.has(FILTER_TYPE.EMPLOYEE)}
              onToggle={() => toggleFilter(FILTER_TYPE.EMPLOYEE)}
              dataTestId={chartModeTestId("sidebar", "box-styles-filter-employee-checkbox")}
            />
            {isPositionChart ? (
              <FilterItem
                label={appstr("Position")}
                checked={activeFilters.has(FILTER_TYPE.POSITION)}
                onToggle={() => toggleFilter(FILTER_TYPE.POSITION)}
                dataTestId={chartModeTestId("sidebar", "box-styles-filter-position-checkbox")}
              />
            ) : (
              <FilterItem
                label={appstr("group")}
                checked={activeFilters.has(FILTER_TYPE.GROUP)}
                onToggle={() => toggleFilter(FILTER_TYPE.GROUP)}
                dataTestId={chartModeTestId("sidebar", "box-styles-filter-group-checkbox")}
              />
            )}
          </FilterSection>
        )}
        <Flex
          justifyContent="flex-end"
          padding={"12px 0 0 0"}
          className="ocn-box-styles-filter-footer"
        >
          <Button
            modifier="secondary"
            size="medium"
            onClick={onResetFilters}
            dataTestId={chartModeTestId("sidebar", "box-styles-filter-reset-btn")}
          >
            {appstr("Reset filters")}
          </Button>
        </Flex>
      </Flex>
    </Card>
  );
};

// Hook for filter logic
export const useFilterBoxStyles = (
  boxStyles: BoxStyleEntry[],
  boxStyleCounts: Map<string, number>,
  isPositionChart: boolean,
  canAccessGrouping: boolean,
  storageKey: string,
  alwaysShowIds: Set<string | number> = new Set()
) => {
  const showTypeFilters = isPositionChart || canAccessGrouping;

  const [activeFilters, setActiveFilters] = useState<Set<FilterKey>>(() => {
    const storedState = getStoredFilterState(storageKey);
    return new Set(storedState?.activeFilters ?? FILTER_KEYS);
  });
  const [searchQuery, setSearchQuery] = useState(
    () => getStoredFilterState(storageKey)?.searchQuery ?? ""
  );
  const searchRef = useRef<any>(null);
  const defaultFilters = new Set(FILTER_KEYS);

  // When the chart changes (storageKey changes), start fresh from that chart's stored state.
  useEffect(() => {
    const storedState = getStoredFilterState(storageKey);
    setActiveFilters(new Set(storedState?.activeFilters ?? FILTER_KEYS));
    setSearchQuery(storedState?.searchQuery ?? "");
  }, [storageKey]);

  useEffect(() => {
    const stateToPersist: StoredFilterState = {
      activeFilters: Array.from(activeFilters),
      searchQuery
    };
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(stateToPersist));
    } catch (_error) {
      // Ignore storage access errors and keep state in-memory.
    }
  }, [activeFilters, searchQuery, storageKey]);

  useEffect(() => {
    if (searchRef.current?.setValue) {
      searchRef.current.setValue(searchQuery, true);
    }
  }, [searchQuery]);

  const toggleFilter = (key: FilterKey) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const resetFilters = () => {
    setActiveFilters(new Set(FILTER_KEYS));
    setSearchQuery("");
  };

  const filtersAreDefault =
    activeFilters.size === defaultFilters.size &&
    Array.from(activeFilters).every(key => defaultFilters.has(key)) &&
    searchQuery === "";

  const searchProps = {
    onInput: (value: string) => setSearchQuery(value),
    onResetInput: () => setSearchQuery(""),
    handleRef: searchRef,
    initialValue: searchQuery
  };

  const filteredBoxStyles = boxStyles.filter(boxStyle => {
    const id = boxStyle.layout.GetBoxStyleID();
    if (alwaysShowIds.has(id)) return true;

    if (searchQuery) {
      const name = boxStyle.layout.GetBoxStyleName().toLowerCase();
      if (!name.includes(searchQuery.toLowerCase())) return false;
    }

    const hasViews = boxStyleCounts.has(id);
    if (!(
      (activeFilters.has(FILTER_TYPE.APPLIED) && hasViews) ||
      (activeFilters.has(FILTER_TYPE.NOT_APPLIED) && !hasViews)
    ))
      return false;

    const isSystem = boxStyle.boxStyleSet === enumBoxStyleSet.systemboxstyles;
    const isShared = boxStyle.boxStyleSet === enumBoxStyleSet.sharedboxstyles;
    if (!(
      (activeFilters.has(FILTER_TYPE.SYSTEM) && isSystem) ||
      (activeFilters.has(FILTER_TYPE.SHARED) && isShared)
    ))
      return false;

    if (showTypeFilters) {
      const hierarchyType = boxStyle.layout.GetHierarchyType() ?? enumHierarchyTypes.employees;
      const isEmployee = hierarchyType === enumHierarchyTypes.employees;
      const isPosition = hierarchyType === enumHierarchyTypes.positions;
      const isGroup = hierarchyType === enumHierarchyTypes.grouping;
      if (!(
        (activeFilters.has(FILTER_TYPE.EMPLOYEE) && isEmployee) ||
        (activeFilters.has(FILTER_TYPE.POSITION) && isPosition) ||
        (activeFilters.has(FILTER_TYPE.GROUP) && isGroup)
      ))
        return false;
    }

    return true;
  });

  return {
    filteredBoxStyles,
    filtersAreDefault,
    searchProps,
    activeFilters,
    toggleFilter,
    onResetFilters: resetFilters
  };
};

export default BoxStyleFilters;
